import { useEffect, useRef, useState } from "react";

/* Words that arrive in bursts — a run's stream merges them per 50 ms — shown at an even
   pace: every frame reveals a slice of what is still hidden, sized so any backlog clears in
   about CATCH_UP_FRAMES frames. A long burst types faster, a trickle letter by letter.
   Not live (or with reduced motion), the text shows as it is. Shared by the dashboard
   chat and the client app, which never import each other. */

const CATCH_UP_FRAMES = 12; // ~200 ms at 60 fps

const reducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

export function useSmoothText(text: string, live: boolean): string {
  const [shown, setShown] = useState(0);
  const target = useRef(text);
  target.current = text;
  const smooth = live && !reducedMotion();

  useEffect(() => {
    if (!smooth) return;
    let frame = requestAnimationFrame(function tick() {
      // A retry starts the words over: never show past what there is.
      setShown((n) => {
        const at = Math.min(n, target.current.length);
        return at + Math.ceil((target.current.length - at) / CATCH_UP_FRAMES);
      });
      frame = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(frame);
  }, [smooth]);

  return smooth ? text.slice(0, shown) : text;
}
